import type { Relation } from '../model';
import { ForestHelper, type Forest } from './forest-helper';
import { GroupRowHelper } from './group-row-helper';
import { LabelPlacementHelper, type LabelText } from './label-placement-helper';
import {
  LayoutGeometryHelper as G,
  type NodeFrame,
} from './layout-geometry-helper';
import { LeafColumnHelper, type LeafArea } from './leaf-column-helper';
import type { SharedPlacement } from './multi-owner-helper';
import { ReferenceChipHelper } from './reference-chip-helper';
import { SharedBandHelper, type BandRowItem } from './shared-band-helper';
import type {
  Block,
  ChipDraft,
  PlacedNode,
  Point,
} from './structure-layout.types';

export interface BlockContext {
  forest: Forest;
  frame: NodeFrame;
  indent: number;
  labelOf(relation: Readonly<Relation>): LabelText | null;
  chipsOf(entityId: number): ChipDraft[];
  gutterOf(entityId: number): number;
  sharedOf(hostId: number, rowIndex: number): SharedPlacement | undefined;
}

interface RowOptions {
  withLines: boolean;
  spine: Point[];
  spineX: number;
  cursorY: number;
  alignAnchorX: number | null;
}

interface PlacedBlock {
  entityId: number;
  x: number;
  block: Block;
}

type Accumulator = Omit<Block, 'width' | 'height' | 'anchorX'>;

class BlockLayoutHelper {
  static layout(entityId: number, ctx: Readonly<BlockContext>): Block {
    const children = ForestHelper.getChildren(ctx.forest, entityId);
    const leaves = children.filter((id) => ForestHelper.isLeaf(ctx.forest, id));
    const groups = children.filter(
      (id) => !ForestHelper.isLeaf(ctx.forest, id)
    );
    const area =
      leaves.length > 0
        ? LeafColumnHelper.arrange(
            leaves.map((id) => ({
              entityId: id,
              footprintHeight:
                ctx.frame.height +
                ReferenceChipHelper.getStackHeight(ctx.chipsOf(id).length),
            })),
            ctx.indent,
            ctx.frame
          )
        : null;
    const rootX =
      area?.mode === 'single' ? -ctx.frame.trunkInset : -ctx.frame.width / 2;
    const root: PlacedNode = {
      entityId,
      x: rootX,
      y: 0,
      width: ctx.frame.width,
      height: ctx.frame.height,
    };
    const rootChips = ReferenceChipHelper.besideNode(
      ctx.chipsOf(entityId),
      root
    );
    const acc: Accumulator = {
      nodes: [root],
      lines: [],
      labels: [],
      chips: [...rootChips],
    };
    const contentTop = Math.max(
      ctx.frame.height,
      ...rootChips.map((chip) => chip.y + chip.height)
    );
    let cursorY = contentTop;
    let spine: Point[] = [{ x: 0, y: ctx.frame.height }];
    let spineX = 0;

    if (area) {
      const leafTop = contentTop + G.FIRST_ROW_GAP;
      BlockLayoutHelper.placeLeaves(area, leafTop, acc, ctx);
      cursorY = leafTop + area.height;
      if (area.mode === 'pairs') {
        spineX = area.corridors.reduce((best, x) =>
          Math.abs(x) < Math.abs(best) ? x : best
        );
        spine = [
          { x: 0, y: ctx.frame.height },
          { x: 0, y: leafTop },
          { x: spineX, y: leafTop },
        ];
      }
    }

    if (groups.length === 1 && !area) {
      BlockLayoutHelper.placeChain(entityId, groups[0], contentTop, acc, ctx);
    } else if (groups.length > 0) {
      const alignAnchorX =
        area?.mode === 'single' && groups.length === 1
          ? rootX + ctx.frame.width / 2
          : null;
      BlockLayoutHelper.placeRows(entityId, groups, acc, ctx, {
        withLines: true,
        spine,
        spineX,
        cursorY,
        alignAnchorX,
      });
    }
    return BlockLayoutHelper.normalize(acc, rootX + ctx.frame.width / 2);
  }

  static layoutForest(
    roots: ReadonlyArray<number>,
    ctx: Readonly<BlockContext>
  ): Block {
    const acc: Accumulator = { nodes: [], lines: [], labels: [], chips: [] };
    if (roots.length > 0) {
      BlockLayoutHelper.placeRows(ForestHelper.VIRTUAL_ROOT, roots, acc, ctx, {
        withLines: false,
        spine: [],
        spineX: 0,
        cursorY: 0,
        alignAnchorX: null,
      });
    }
    return BlockLayoutHelper.normalize(acc, 0);
  }

  private static placeLeaves(
    area: Readonly<LeafArea>,
    leafTop: number,
    acc: Accumulator,
    ctx: Readonly<BlockContext>
  ): void {
    area.placements.forEach((placement) => {
      const node: PlacedNode = {
        entityId: placement.entityId,
        x: placement.x,
        y: leafTop + placement.y,
        width: ctx.frame.width,
        height: ctx.frame.height,
      };
      acc.nodes.push(node);
      acc.chips.push(
        ...ReferenceChipHelper.belowNode(ctx.chipsOf(placement.entityId), node)
      );
      const relation = BlockLayoutHelper.getPrimaryRelation(
        placement.entityId,
        ctx
      );
      const branch = LeafColumnHelper.getBranchPoints(
        placement,
        area,
        ctx.frame
      ).map((point) => ({ x: point.x, y: point.y + leafTop }));
      acc.lines.push({
        relationId: relation.id,
        kind: relation.kind,
        points: [{ x: 0, y: ctx.frame.height }, ...branch],
      });
      const label = ctx.labelOf(relation);
      if (label)
        acc.labels.push(
          LabelPlacementHelper.onBranch(
            relation.id,
            label,
            placement.trunkX,
            node.y + ctx.frame.height / 2,
            placement.side
          )
        );
    });
  }

  private static placeChain(
    entityId: number,
    childId: number,
    contentTop: number,
    acc: Accumulator,
    ctx: Readonly<BlockContext>
  ): void {
    const child = BlockLayoutHelper.layout(childId, ctx);
    const top = contentTop + G.CHAIN_GAP;
    const x = -child.anchorX;
    BlockLayoutHelper.merge(acc, G.translateBlock(child, x, top));
    const relation = BlockLayoutHelper.getPrimaryRelation(childId, ctx);
    acc.lines.push({
      relationId: relation.id,
      kind: relation.kind,
      points: [
        { x: 0, y: ctx.frame.height },
        { x: 0, y: top },
      ],
    });
    const label = ctx.labelOf(relation);
    if (label)
      acc.labels.push(
        LabelPlacementHelper.besideDrop(
          relation.id,
          label,
          0,
          ctx.frame.height,
          top
        )
      );
    const shared = ctx.sharedOf(entityId, 0);
    if (shared)
      BlockLayoutHelper.placeBand(
        shared,
        [{ entityId: childId, x, width: child.width }],
        top + child.height,
        acc,
        ctx
      );
  }

  private static placeRows(
    hostId: number,
    childIds: ReadonlyArray<number>,
    acc: Accumulator,
    ctx: Readonly<BlockContext>,
    options: Readonly<RowOptions>
  ): void {
    const gap = options.withLines ? G.GROUP_GAP : G.ROOT_GAP;
    const rows = GroupRowHelper.chunk(
      childIds.map((id) => ({
        entityId: id,
        block: BlockLayoutHelper.layout(id, ctx),
      })),
      G.GROUPS_PER_ROW
    );
    let { cursorY, spine, spineX } = options;
    rows.forEach((row, rowIndex) => {
      const busY =
        cursorY + (rowIndex === 0 && !options.withLines ? 0 : G.BUS_GAP);
      const top = options.withLines
        ? busY + G.BUS_DROP
        : cursorY + (rowIndex === 0 ? 0 : G.ROOT_ROW_GAP);
      const xs = GroupRowHelper.place(
        row.map((item) => ({
          width: item.block.width,
          rightGutter: ctx.gutterOf(item.entityId),
        })),
        gap
      );
      const firstAnchor = xs[0] + row[0].block.anchorX;
      const lastAnchor = xs[xs.length - 1] + row[row.length - 1].block.anchorX;
      let originX = 0;
      if (options.withLines) {
        originX =
          options.alignAnchorX === null
            ? spineX - Math.round((firstAnchor + lastAnchor) / 2)
            : options.alignAnchorX - firstAnchor;
      }
      const placed: PlacedBlock[] = row.map((item, i) => ({
        entityId: item.entityId,
        x: originX + xs[i],
        block: item.block,
      }));
      placed.forEach((item) => {
        BlockLayoutHelper.merge(acc, G.translateBlock(item.block, item.x, top));
        if (!options.withLines) return;
        const anchorX = item.x + item.block.anchorX;
        const relation = BlockLayoutHelper.getPrimaryRelation(
          item.entityId,
          ctx
        );
        acc.lines.push({
          relationId: relation.id,
          kind: relation.kind,
          points: [
            ...spine,
            { x: spineX, y: busY },
            { x: anchorX, y: busY },
            { x: anchorX, y: top },
          ],
        });
        const label = ctx.labelOf(relation);
        if (label)
          acc.labels.push(
            LabelPlacementHelper.besideDrop(
              relation.id,
              label,
              anchorX,
              busY,
              top
            )
          );
      });
      let rowBottom =
        top + Math.max(...placed.map((item) => item.block.height));
      const shared = ctx.sharedOf(hostId, rowIndex);
      if (shared) {
        const items = placed.map(
          (item): BandRowItem => ({
            entityId: item.entityId,
            x: item.x,
            width: item.block.width,
          })
        );
        rowBottom = BlockLayoutHelper.placeBand(
          shared,
          items,
          rowBottom,
          acc,
          ctx
        );
      }
      if (options.withLines && rowIndex < rows.length - 1) {
        const gapX =
          GroupRowHelper.nearestGapX(
            placed.map((item) => ({ x: item.x, width: item.block.width })),
            spineX
          ) ?? spineX;
        spine = [...spine, { x: spineX, y: busY }, { x: gapX, y: busY }];
        spineX = gapX;
      }
      cursorY = rowBottom;
    });
  }

  private static placeBand(
    placement: Readonly<SharedPlacement>,
    row: ReadonlyArray<BandRowItem>,
    rowBottom: number,
    acc: Accumulator,
    ctx: Readonly<BlockContext>
  ): number {
    const child = BlockLayoutHelper.layout(placement.entityId, ctx);
    const nodeOf = (id: number) =>
      acc.nodes.find((node) => node.entityId === id);
    const band = SharedBandHelper.place(
      placement,
      row,
      nodeOf,
      child,
      rowBottom,
      ctx.labelOf,
      ctx.frame
    );
    BlockLayoutHelper.merge(
      acc,
      G.translateBlock(child, band.childX, band.childTop)
    );
    acc.lines.push(...band.lines);
    acc.labels.push(...band.labels);
    return band.childTop + child.height;
  }

  private static getPrimaryRelation(
    entityId: number,
    ctx: Readonly<BlockContext>
  ): Relation {
    const relation = ctx.forest.primaryRelationOf.get(entityId);
    if (!relation)
      throw new Error(
        `Entity ${entityId} is placed as a child but has no primary owner`
      );
    return relation;
  }

  private static merge(acc: Accumulator, block: Readonly<Block>): void {
    acc.nodes.push(...block.nodes);
    acc.lines.push(...block.lines);
    acc.labels.push(...block.labels);
    acc.chips.push(...block.chips);
  }

  private static normalize(acc: Readonly<Accumulator>, anchorX: number): Block {
    const bounds = G.unionRects([
      ...acc.nodes,
      ...acc.chips,
      ...acc.labels,
      ...acc.lines.map((line) => G.pointsToRect(line.points)),
    ]);
    const block: Block = {
      ...acc,
      width: bounds.width,
      height: bounds.height,
      anchorX: anchorX - bounds.x,
    };
    return G.translateBlock(block, -bounds.x, -bounds.y);
  }
}

export { BlockLayoutHelper };
